import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { Phone, PhoneCall, PhoneOff, PhoneMissed, Clock, Users, Network, List, TrendingUp, Activity,
  Cpu, HardDrive, Server, Shield, ShieldAlert, Ban, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import { pbxWS } from '../../services/websocket';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import EventLogPanel from '../events/EventLogPanel';

const COLORS = ['#22c55e', '#ef4444', '#f59e0b', '#3b82f6'];

/* ─── Helpers de formato ─────────────────────────────────────── */
const fmtBytes = (b) => {
  if (!b && b !== 0) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
};
const fmtUptime = (s) => {
  if (!s) return '—';
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
};
const barColor = (p) => p >= 90 ? 'bg-red-500' : p >= 75 ? 'bg-amber-500' : 'bg-blue-500';
const txtColor = (p) => p >= 90 ? 'text-red-400' : p >= 75 ? 'text-amber-400' : 'text-gray-200';

/* ─── Barra de uso de recurso ────────────────────────────────── */
function ResourceBar({ icon: Icon, label, percent, detail }) {
  const p = Math.max(0, Math.min(100, percent ?? 0));
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon size={13} className="text-muted shrink-0" />
          <span className="text-xs text-gray-400 truncate">{label}</span>
        </div>
        <span className={`text-xs font-semibold shrink-0 ${txtColor(p)}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {p}%
        </span>
      </div>
      <div className="w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
        <div className={`h-1.5 rounded-full transition-all duration-500 ${barColor(p)}`} style={{ width: `${p}%` }} />
      </div>
      {detail && <p className="text-[11px] text-muted mt-1 truncate">{detail}</p>}
    </div>
  );
}

/* Tarjeta KPI — estilo unificado del panel:
   etiqueta en mayúsculas, número grande, ícono difuminado y línea de contexto. */
function StatCard({ icon: Icon, label, value, sub, color = 'blue', trend }) {
  const valueColor = {
    blue: 'text-blue-400',
    green: 'text-green-400',
    red: 'text-red-400',
    yellow: 'text-yellow-400',
    purple: 'text-purple-400',
    white: 'text-white',
  }[color] || 'text-white';

  return (
    <div className="relative rounded-xl p-4 bg-gray-900/60 border border-gray-800 overflow-hidden">
      <Icon size={76} className="absolute -right-3 -top-3 text-white opacity-[0.04] pointer-events-none" />
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider text-muted font-medium">{label}</span>
        {trend !== undefined && (
          <span className={`text-xs font-semibold shrink-0 ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className={`text-[34px] leading-tight font-bold mt-0.5 ${valueColor}`}
           style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {sub && (
        <div className="flex items-center gap-1.5 mt-2 text-xs text-muted">
          <Icon size={12} className="shrink-0" />
          <span className="truncate">{sub}</span>
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const [activeCalls, setActiveCalls] = useState([]);

  const { data: stats, refetch: refetchStats } = useQuery(
    'dashboard-stats',
    () => api.get('/dashboard/stats').then(r => r.data),
    { refetchInterval: 30000 }
  );

  const { data: chart } = useQuery(
    'dashboard-chart',
    () => api.get('/dashboard/calls-chart?period=today&type=hourly').then(r => r.data),
    { refetchInterval: 60000 }
  );

  const { data: topExt } = useQuery(
    'dashboard-top-extensions',
    () => api.get('/dashboard/top-extensions').then(r => r.data),
    { refetchInterval: 120000 }
  );

  // Monitor de recursos del VPS (refresca cada 10s)
  const { data: resources } = useQuery(
    'system-resources',
    () => api.get('/system/resources').then(r => r.data),
    { refetchInterval: 10000, refetchOnWindowFocus: false }
  );

  // Monitor global de seguridad (refresca cada 30s)
  const { data: security } = useQuery(
    'system-security',
    () => api.get('/system/security').then(r => r.data),
    { refetchInterval: 30000, refetchOnWindowFocus: false }
  );

  const { data: queueStats } = useQuery(
    'dashboard-queues',
    () => api.get('/dashboard/queue-stats').then(r => r.data),
    { refetchInterval: 15000 }
  );

  useEffect(() => {
    // Actualizar llamadas activas via WS
    const offNew = pbxWS.on('call.new', (call) => {
      setActiveCalls(prev => [...prev, call]);
      refetchStats();
    });
    const offHangup = pbxWS.on('call.hangup', (call) => {
      setActiveCalls(prev => prev.filter(c => c.uniqueID !== call.uniqueID));
      refetchStats();
    });
    return () => { offNew(); offHangup(); };
  }, [refetchStats]);

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}m ${s}s`;
  };

  const pieData = stats ? [
    { name: 'Contestadas', value: stats.calls.today.answered },
    { name: 'Perdidas', value: stats.calls.today.missed },
    { name: 'Ocupado', value: stats.calls.today.busy },
  ] : [];

  const chartData = (chart || []).map(row => ({
    time: format(new Date(row.period), 'HH:mm', { locale: es }),
    total: parseInt(row.total),
    answered: parseInt(row.answered),
    missed: parseInt(row.missed),
    inbound: parseInt(row.inbound),
    outbound: parseInt(row.outbound),
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400 text-sm mt-1">
            {format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
          </p>
        </div>
        <div className="flex items-center gap-2 bg-gray-900 border border-gray-800 px-3 py-2 rounded-lg">
          <Activity size={16} className="text-green-400 animate-pulse" />
          <span className="text-sm text-gray-300">
            {stats?.calls?.active || 0} llamada{stats?.calls?.active !== 1 ? 's' : ''} activa{stats?.calls?.active !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={PhoneCall}
          label="Total Hoy"
          value={stats?.calls?.today?.total || 0}
          sub={`Tasa respuesta: ${stats?.calls?.today?.answerRate || 0}%`}
          color="blue"
        />
        <StatCard
          icon={Phone}
          label="Contestadas"
          value={stats?.calls?.today?.answered || 0}
          sub={`Duración prom: ${formatDuration(stats?.calls?.today?.avgDuration || 0)}`}
          color="green"
        />
        <StatCard
          icon={PhoneMissed}
          label="Perdidas"
          value={stats?.calls?.today?.missed || 0}
          sub="Llamadas sin respuesta"
          color="red"
        />
        <StatCard
          icon={Clock}
          label="Tiempo Total"
          value={formatDuration(stats?.calls?.today?.totalDuration || 0)}
          sub="Tiempo en llamadas hoy"
          color="purple"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Extensiones Activas" value={stats?.extensions?.active || 0} sub={`de ${stats?.extensions?.total || 0} total`} color="blue" />
        <StatCard icon={Network} label="Troncales Activas" value={stats?.trunks?.active || 0} sub={`de ${stats?.trunks?.total || 0} total`} color="green" />
        <StatCard icon={List} label="Colas ACD" value={stats?.queues?.total || 0} sub="Colas configuradas" color="yellow" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gráfico de llamadas por hora */}
        <div className="card lg:col-span-2">
          <h3 className="text-base font-semibold text-white mb-4">Llamadas por Hora — Hoy</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="gradAnswered" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis dataKey="time" tick={{ fill: '#6b7280', fontSize: 11 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: '8px' }}
                labelStyle={{ color: '#f1f5f9' }}
              />
              <Area type="monotone" dataKey="total" stroke="#3b82f6" fill="url(#gradTotal)" strokeWidth={2} name="Total" />
              <Area type="monotone" dataKey="answered" stroke="#22c55e" fill="url(#gradAnswered)" strokeWidth={2} name="Contestadas" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Distribución */}
        <div className="card">
          <h3 className="text-base font-semibold text-white mb-4">Distribución</h3>
          {pieData.some(d => d.value > 0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="45%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value">
                  {pieData.map((_, index) => (
                    <Cell key={index} fill={COLORS[index]} />
                  ))}
                </Pie>
                <Legend iconType="circle" iconSize={8}
                  formatter={(value) => <span className="text-gray-400 text-xs">{value}</span>}
                />
                <Tooltip contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: '8px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-muted text-sm">
              Sin datos hoy
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top extensiones */}
        <div className="card">
          <h3 className="text-base font-semibold text-white mb-4">Top Extensiones (7 días)</h3>
          <div className="space-y-2">
            {(topExt || []).slice(0, 8).map((ext, i) => (
              <div key={ext.extension} className="flex items-center gap-3">
                <span className="text-xs text-muted w-4">{i + 1}</span>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="w-7 h-7 bg-blue-600/20 rounded-lg flex items-center justify-center text-xs text-blue-400 font-medium">
                    {ext.extension}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-300">Ext. {ext.extension}</span>
                      <span className="text-sm text-gray-400">{ext.total_calls} llamadas</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-1 mt-1">
                      <div
                        className="bg-blue-500 h-1 rounded-full"
                        style={{ width: `${Math.min(100, (ext.total_calls / (topExt[0]?.total_calls || 1)) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {!topExt?.length && <p className="text-muted text-sm">Sin datos disponibles</p>}
          </div>
        </div>

        {/* Estado de colas */}
        <div className="card">
          <h3 className="text-base font-semibold text-white mb-4">Estado de Colas</h3>
          <div className="space-y-3">
            {(queueStats || []).map(q => (
              <div key={q.id} className="bg-gray-800/50 rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-gray-200">{q.name}</p>
                    <p className="text-xs text-muted">Ext. {q.number}</p>
                  </div>
                  <span className={`badge-${q.available_agents > 0 ? 'success' : 'danger'}`}>
                    {q.available_agents > 0 ? 'Activa' : 'Sin agentes'}
                  </span>
                </div>
                <div className="flex gap-4 mt-2">
                  <span className="text-xs text-gray-400">
                    Agentes: <strong className="text-gray-200">{q.available_agents}/{q.total_agents}</strong>
                  </span>
                </div>
              </div>
            ))}
            {!queueStats?.length && <p className="text-muted text-sm">Sin colas configuradas</p>}
          </div>
        </div>
      </div>

      {/* Monitor de recursos del VPS + Seguridad global */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Recursos del VPS ── */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Server size={16} className="text-blue-400" /> Recursos del VPS
            </h3>
            {resources && (
              <span className="text-[11px] text-muted">
                Activo {fmtUptime(resources.uptimeSeconds)}
              </span>
            )}
          </div>

          {!resources ? (
            <p className="text-muted text-sm">Cargando métricas…</p>
          ) : (
            <div className="space-y-4">
              <ResourceBar
                icon={Cpu} label={`CPU · ${resources.cpu?.cores} núcleos`}
                percent={resources.cpu?.usagePercent}
                detail={`Carga: ${resources.cpu?.loadavg?.join(' · ')} (1/5/15 min)`}
              />
              <ResourceBar
                icon={Activity} label="Memoria RAM"
                percent={resources.memory?.percent}
                detail={`${fmtBytes(resources.memory?.used)} usados de ${fmtBytes(resources.memory?.total)}`}
              />
              {resources.disk ? (
                <ResourceBar
                  icon={HardDrive} label="Disco"
                  percent={resources.disk?.percent}
                  detail={`${fmtBytes(resources.disk?.used)} usados de ${fmtBytes(resources.disk?.total)} · ${fmtBytes(resources.disk?.free)} libres`}
                />
              ) : (
                <p className="text-xs text-muted">Disco: métrica no disponible</p>
              )}
              <p className="text-[11px] text-muted pt-1 border-t border-gray-800 truncate">
                {resources.platform} · {resources.cpu?.model}
              </p>
            </div>
          )}
        </div>

        {/* ── Seguridad global ── */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Shield size={16} className="text-green-400" /> Seguridad global
            </h3>
            {security?.available && (
              <span className="text-[11px] text-muted">fail2ban</span>
            )}
          </div>

          {!security ? (
            <p className="text-muted text-sm">Cargando…</p>
          ) : !security.available ? (
            <div className="flex items-start gap-2 text-xs text-amber-400 bg-amber-900/15 border border-amber-700/30 rounded-lg p-3">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{security.reason || 'Monitor de seguridad no disponible'}</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Resumen */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Baneadas', value: security.totalCurrentlyBanned, color: 'text-red-400', Icon: Ban },
                  { label: 'Ataques 24h', value: security.bans24h, color: 'text-amber-400', Icon: ShieldAlert },
                  { label: 'Atacantes', value: security.uniqueAttackers, color: 'text-gray-200', Icon: AlertTriangle },
                ].map(s => (
                  <div key={s.label} className="bg-gray-800/50 rounded-lg p-2.5 text-center">
                    <s.Icon size={13} className={`${s.color} mx-auto mb-1`} />
                    <div className={`text-xl font-bold ${s.color}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {s.value ?? 0}
                    </div>
                    <div className="text-[11px] uppercase tracking-wider text-muted mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Jails */}
              {security.jails?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted mb-2">Protecciones activas</p>
                  <div className="space-y-1.5">
                    {security.jails.slice(0, 5).map(j => (
                      <div key={j.name} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-gray-300 truncate font-mono">{j.name}</span>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-muted">{j.banned24h} en 24h</span>
                          <span className={`px-1.5 py-0.5 rounded font-semibold ${
                            j.currentlyBanned > 0 ? 'bg-red-900/40 text-red-300' : 'bg-gray-800 text-muted'
                          }`}>
                            {j.currentlyBanned} activos
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Últimos baneos */}
              {security.recentBans?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted mb-2">Últimos bloqueos</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {security.recentBans.slice(0, 6).map((b, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-[11px]">
                        <span className="font-mono text-gray-400 truncate">{b.ip}</span>
                        <span className="text-muted truncate">{b.jail}</span>
                        <span className="text-gray-700 shrink-0">{b.time?.slice(5, 16)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Registro de eventos: lo que antes solo se veía entrando por SSH a
          los logs de Asterisk (troncal caída, tope de canales, escuchas
          fallidas) queda a la vista en la primera pantalla. */}
      <EventLogPanel compact />
    </div>
  );
}
